import { ThemeEvents } from '@theme/events';

/**
 * Custom element for the Cart offer popup section. Each instance lives on a
 * specific product template (added by the merchant via the theme editor), so
 * the trigger is simply "the current page's product was added to cart" — no
 * separate trigger-type/collection setting needed. Shows once per page load,
 * and only if none of the offered products already has a variant in the cart.
 *
 * The offer is built from theme blocks dropped into the section — normally the
 * same "Flow Star bundle" block the product page uses — so this element has no
 * configured list of products. It reads whatever was rendered inside it:
 * product ids from each nested product-form-component, variant ids from each
 * bundle card's variants JSON. Swap the blocks and the logic follows along.
 */
class CartOfferPopup extends HTMLElement {
  #hasShown = false;
  #abortController = new AbortController();

  connectedCallback() {
    this.triggerProductId = this.dataset.triggerProductId || '';
    if (!this.triggerProductId) return;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    return this.querySelector('dialog');
  }

  /**
   * Product ids of everything offered inside the popup, read from the add-to-cart
   * forms the blocks rendered.
   * @returns {Set<string>}
   */
  #offerProductIds() {
    const ids = new Set();
    this.querySelectorAll('[data-product-id]').forEach((el) => {
      const id = el.getAttribute('data-product-id');
      if (id) ids.add(String(id));
    });
    return ids;
  }

  /**
   * Every variant id of everything offered inside the popup, read from the
   * bundle cards' own variants JSON.
   * @returns {Set<string>}
   */
  #offerVariantIds() {
    const ids = new Set();
    this.querySelectorAll('[data-fsb-variants]').forEach((script) => {
      try {
        const variants = JSON.parse(script.textContent ?? '[]');
        if (Array.isArray(variants)) variants.forEach((variant) => ids.add(String(variant.id)));
      } catch (error) {
        // A malformed card should never take the whole popup down with it.
      }
    });
    return ids;
  }

  /** @param {CustomEvent} event */
  #handleCartUpdate = async (event) => {
    const data = event.detail?.data;
    if (!data || data.didError) return;
    if (data.source !== 'product-form-component') return;

    const addedProductId = data.productId ? String(data.productId) : '';
    if (!addedProductId) return;

    // Adding one of the offers from inside the open popup closes it and hands
    // over to the cart drawer. Deferred a tick so the theme finishes its own
    // add-to-cart handling — closing mid-flight fights the dialog's scroll-lock.
    // setTimeout rather than requestAnimationFrame: rAF is frozen while a tab is
    // hidden, and the popup should still tidy itself up if the shopper switches
    // away mid-add.
    const dialog = this.#dialog;
    if (dialog?.open && this.#offerProductIds().has(addedProductId)) {
      setTimeout(() => {
        const dialogComponent = this.querySelector('dialog-component');
        if (typeof dialogComponent?.closeDialog === 'function') {
          dialogComponent.closeDialog();
        } else {
          dialog.close();
        }
      }, 0);
      return;
    }

    if (this.#hasShown) return;
    if (addedProductId !== this.triggerProductId) return;

    const offerVariantIds = this.#offerVariantIds();
    if (offerVariantIds.size === 0) return;

    let cart;
    try {
      const response = await fetch('/cart.js');
      cart = await response.json();
    } catch (error) {
      return;
    }

    const alreadyHasOffer = cart.items.some((item) => offerVariantIds.has(String(item.variant_id)));
    if (alreadyHasOffer) return;

    this.#hasShown = true;

    const dialogComponent = this.querySelector('dialog-component');
    dialogComponent?.showDialog?.();
  };
}

if (!customElements.get('cart-offer-popup')) {
  customElements.define('cart-offer-popup', CartOfferPopup);
}
